// Cookie & Privacy Management
import * as klaro from "klaro/dist/klaro-no-css";
import tippy from 'tippy.js';

import Alpine from 'alpinejs';
import persist from '@alpinejs/persist'

const config = {
    storageMethod: 'cookie',
    cookieName: process.env.COOKIE_NAME,
    cookieExpiresAfterDays: 365,
    cookieDomain: process.env.COOKIE_DOMAIN,
    cookiePath: '/',

    mustConsent: true,
    collapseConsentOptions: true,
    noticeAsModal: false,

    acceptAll: true,
    hideDeclineAll: true,

    disablePoweredBy: true,
    default: false,

    styling: {
        theme: ['light', 'bottom']
    },
    translations: {
        en: {
            purposes: {
                session: {
                    title: "Session",
                    description: "These cookies are needed for essential functions such as making payments. Standard cookies can’t be switched off and they don’t store any of your information.​"
                },
                analytics: {
                    title: "Analytics",
                    description: 'These cookies gather information such as how many people are using our site or which pages are popular to help us improve customer experience. Switching off these cookies will mean we can’t gather information to improve the experience.'
                },
                advertising: {
                    title: "Advertising",
                    description: 'These cookies help us to learn what you’re interested in so we can show you relevant adverts on other websites. Switching off these cookies will mean we can’t show you any personalised adverts.​'
                }
            },
            ok: 'Accept All',
            acceptAll: 'Accept All',
            acceptSelected: 'Accept Selected',
            save: 'Accept Selected',
            decline: 'Essential Only',
            consentNotice: {
                learnMore: 'Learn more',
                description: 'We use cookies to improve your experience on our website and to show you personalised content. You can allow all or manage them individually.'
            },
            consentModal: {
                title: 'Cookies',
                description: 'We use cookies to improve your experience on our website and to show you personalised content. You can allow all or manage them individually.'
            },
            privacyPolicyUrl: process.env.COOKIE_PRIVACY_URL
        }
    },
    services: [
        {
            name: "application",
            title: "Application Session",
            description: "Required for core application functionality including cart, logins and consent management.",
            purposes: ["session"],
            cookies: [
                [/^(.*)powerbulbs_pb_session$/, '/', process.env.COOKIE_DOMAIN],
                [/^(.*)powerbulbs_customer_email$/, '/', process.env.COOKIE_DOMAIN],
                [/^(.*)powerbulbs_powerbulbs_cookie_email$/, '/', process.env.COOKIE_DOMAIN],
                [/^(.*)pb_cookie_compliance$/, '/', process.env.COOKIE_DOMAIN]
                [/^(.*)totalview_pb_session$/, '/', process.env.COOKIE_DOMAIN]
            ],
            required: true
        },
        {
            name: "application",
            title: "hCaptcha",
            description: "Required to protect the login, account creation and checkout facilities of our website application.",
            purposes: ["session"],
            cookies: [
                ['session', '/', '.hcaptcha.com']
            ],
            required: true
        },
        {
            name: "application",
            title: "reCaptcha",
            description: "Required to protect the login, account creation and checkout facilities of our website application.",
            purposes: ["session"],
            cookies: [
                ['session', '/recaptcha', 'www.recaptcha.net']
            ],
            required: true
        },
        {
            name: "application",
            title: "PayPal",
            description: "Required to enable payments on checkout, these cookies are managed separately on the PayPal popup window.",
            purposes: ["session"],
            cookies: [
                ['paypal', '/', '.paypal.com']
            ],
            required: true
        },
        {
            name: "application",
            title: "CloudFlare",
            description: "Web application firewall, bot protection and security.",
            purposes: ["session"],
            cookies: [
                [
                    /^__cf_(.*)$/,
                    /^__cflb(.*)$/,
                    /^__cf_bm(.*)$/,
                    /^__cf_ob_info(.*)$/,
                    /^__cf_use_ob(.*)$/,
                    /^__cfwaitingroom(.*)$/,
                    /^__cfruid(.*)$/,
                    /^cf_clearance(.*)$/,
                    /^cf-cc-(.*)$/,
                    /^cf-chl-cc-(.*)$/,
                    /^cf-chl-seq-(.*)$/,
                    /^cf-chl-prog(.*)$/,
                    /^cf-chl-(.*)$/,
                    /^cf-chl-rc-i(.*)$/,
                    /^cf-chl-rc-ni(.*)$/
                ]
            ],
            required: true
        },
        {
            name: 'google-tag-manager',
            purposes: ['analytics'],
            onAccept: `
                // we notify the tag manager about all services that were accepted. You can define
                // a custom event in GTM to load the service if consent was given.
                for(let k of Object.keys(opts.consents)){
                    if (opts.consents[k]){
                        let eventName = 'klaro-'+k+'-accepted'
                        dataLayer.push({'event': eventName})
                    }
                }
                // if consent for Google Analytics was granted we enable analytics storage
                if (opts.consents[opts.vars.googleAnalyticsName || 'google-analytics']){
                    gtag('consent', 'update', {'analytics_storage': 'granted'})
                }
                // if consent for Google Ads was granted we enable ad storage
                if (opts.consents[opts.vars.adStorageName || 'google-ads']){
                    gtag('consent', 'update', {'ad_storage': 'granted'})
                }
            `,
            onInit: `
                // initialization code here (will be executed only once per page-load)
                window.dataLayer = window.dataLayer || [];
                window.gtag = function(){dataLayer.push(arguments)}
                gtag('consent', 'default', {'ad_storage': 'denied', 'analytics_storage': 'denied'})
                gtag('set', 'ads_data_redaction', true)
            `,
            onDecline: `
                // initialization code here (will be executed only once per page-load)
                window.dataLayer = window.dataLayer || [];
                window.gtag = function(){dataLayer.push(arguments)}
                gtag('consent', 'default', {'ad_storage': 'denied', 'analytics_storage': 'denied'})
                gtag('set', 'ads_data_redaction', true)
            `,
            vars: {
                googleAnalytics: 'google-analytics'
            },
            cookies: [
                [/^_ga(_.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^__hj(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_hj(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                ['_gid', '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            // In GTM, you should define a custom event trigger named `klaro-google-analytics-accepted` which should trigger the Google Analytics integration.
            name: 'google-analytics',
            purposes: ['analytics'],
            cookies: [
                [/^_ga(_.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_ga(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^__hj(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_hj(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                ['_gid', '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
            // TODO onAccept enable GTAG cookie and warn user as it's a dependant service
        },
        {
            name: 'bing-analytics',
            purposes: ['analytics'],
            cookies: [
                [/^_uet(_.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_uet(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            name: 'ms-clarity',
            purposes: ['analytics'],
            cookies: [
                [/^_clsk(_.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_clck(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            name: 'hotjar',
            purposes: ['analytics'],
            cookies: [
                [/^_hjid(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            name: 'google-certified-shop',
            purposes: ['advertising'],
            cookies: []
        },
        {
            name: 'facebook-pixel',
            purposes: ['advertising'],
            cookies: [
                [/^_fbp(.*)$/, '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            name: 'google-ads',
            purposes: ['advertising'],
            cookies: [
                [/^_gcl(_.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN],
                [/^_gcl(.*)?/, '/', process.env.COOKIE_ROOT_DOMAIN]
            ]
        },
        {
            name: 'social-buttons',
            purposes: ['analytics', 'advertising'],
            cookies: [
                [/^(.*)?/, '/', '.twitter.com']
                [/^(.*)?/, '/', '.facebook.net']
            ]
        },
        {
            name: 'trust-pilot-box',
            purposes: ['analytics', 'advertising'],
            cookies: [
                [/^_csrf$/, '/', 'widget.trustpilot.com'],
                [/^TP.uuid$/, '/', 'widget.trustpilot.com'],
                [/^OptanonConsent$/, '/', 'widget.trustpilot.com']
            ],
            callback: function(consent, service) {
                // This is an example callback function for a particular service consent being changed
            }
        }
    ],

    callback: function(consent, service) {
        // This is an example callback function, will run for every service updated.
    }
};

window.klaro = klaro;
window.klaroConfig = config;
klaro.setup(config);
klaro.getManager(config).watch({
    update(o,n,d) {

        // Reset consent, refresh browser to show deleted cookies and disable JS scripts
        if(n === 'applyConsents' && typeof o === 'object' && o.changed === false && d === Object.keys(o.consents).length) {
            window.klaro.resetManagers();
            location.reload();
        }

        // Save consent, refresh browser to show deleted cookies and disable active JS scripts
        if(n === 'saveConsents' && typeof d === 'object' && d.hasOwnProperty('changes') && Object.keys(d.changes).some(k => !d.changes[k])) {
            location.reload();
        }
    }
});
window.tippy = tippy;

tippy('[data-tippy-content]');

window.Alpine = Alpine;
Alpine.plugin(persist);
Alpine.start();

import './../scss/app.scss';
import './../tail/app.scss';
import 'tippy.js/dist/tippy.css';
